<template>
  <div id="dialog-curriculumAdmin">
    <div class="curriculum-admin">

      <div class="header">
        <p @click="windowOnOff(false)"><i class="ph-bold ph-arrow-left"></i></p>
        <h2>커리큘럼 관리</h2>
        <p @click="showHistory = true"><i class="ph-bold ph-chart-bar"></i></p>
      </div>

      <!-- 수강 현황 -->
      <CurriculumHistory v-if="showHistory" :windowOnOff="(b) => showHistory = b" />

      <div class="content">
        <!-- 상단 액션 바 -->
        <div class="action-bar">
          <button class="add-category-btn" @click="openAddModal('category', null)">
            <i class="ph ph-plus"></i>
            카테고리 추가
          </button>
        </div>

        <!-- 카테고리 목록 -->
        <div class="tree-container">
          <div 
            v-for="category in treeData" 
            :key="category.idx" 
            class="tree-category"
          >
            <div 
              class="tree-item category-item"
              :class="{ 'dragging': dragItem?.idx === category.idx }"
              draggable="true"
              @dragstart="onDragStart($event, category, 'category')"
              @dragover="onDragOver($event, category, 'category')"
              @drop="onDrop($event, category, 'category')"
              @dragend="onDragEnd"
            >
              <div class="item-left">
                <i class="ph ph-dots-six-vertical drag-handle"></i>
                <button class="toggle-btn" @click="toggleExpand(category.idx)">
                  <i :class="expanded[category.idx] ? 'ph ph-caret-down' : 'ph ph-caret-right'"></i>
                </button>
                <span class="item-title">{{ category.title }}</span>
                <span class="item-count">{{ getChapterCount(category) }}개 챕터</span>
              </div>
              <div class="item-actions">
                <button @click="openAddModal('chapter', category.idx)" title="챕터 추가">
                  <i class="ph ph-plus"></i>
                </button>
                <button @click="openEditModal(category)" title="수정">
                  <i class="ph ph-pencil"></i>
                </button>
                <button @click="deleteItem(category)" title="삭제" class="danger">
                  <i class="ph ph-trash"></i>
                </button>
              </div>
            </div>

            <!-- 챕터 목록 -->
            <div v-show="expanded[category.idx]" class="tree-children">
              <div 
                v-for="chapter in category.children" 
                :key="chapter.idx" 
                class="tree-chapter"
              >
                <div 
                  class="tree-item chapter-item"
                  :class="{ 'dragging': dragItem?.idx === chapter.idx }"
                  draggable="true"
                  @dragstart="onDragStart($event, chapter, 'chapter')"
                  @dragover="onDragOver($event, chapter, 'chapter')"
                  @drop="onDrop($event, chapter, 'chapter')"
                  @dragend="onDragEnd"
                >
                  <div class="item-left">
                    <i class="ph ph-dots-six-vertical drag-handle"></i>
                    <button class="toggle-btn" @click="toggleExpand(chapter.idx)">
                      <i :class="expanded[chapter.idx] ? 'ph ph-caret-down' : 'ph ph-caret-right'"></i>
                    </button>
                    <span class="item-title">{{ chapter.title }}</span>
                    <span class="item-count">{{ getLessonCount(chapter) }}개 레슨</span>
                  </div>
                  <div class="item-actions">
                    <button @click="openAddModal('lesson', chapter.idx)" title="레슨 추가">
                      <i class="ph ph-plus"></i>
                    </button>
                    <button @click="openEditModal(chapter)" title="수정">
                      <i class="ph ph-pencil"></i>
                    </button>
                    <button @click="deleteItem(chapter)" title="삭제" class="danger">
                      <i class="ph ph-trash"></i>
                    </button>
                  </div>
                </div>

                <!-- 레슨 목록 -->
                <div v-show="expanded[chapter.idx]" class="tree-children">
                  <div 
                    v-for="lesson in chapter.children" 
                    :key="lesson.idx"
                    class="tree-item lesson-item"
                    :class="{ 'dragging': dragItem?.idx === lesson.idx }"
                    draggable="true"
                    @dragstart="onDragStart($event, lesson, 'lesson')"
                    @dragover="onDragOver($event, lesson, 'lesson')"
                    @drop="onDrop($event, lesson, 'lesson')"
                    @dragend="onDragEnd"
                  >
                    <div class="item-left">
                      <i class="ph ph-dots-six-vertical drag-handle"></i>
                      <i :class="lesson.content_type === 'video' ? 'ph ph-play-circle' : 'ph ph-article'" class="type-icon"></i>
                      <span class="item-title">{{ lesson.title }}</span>
                      <span class="item-duration" v-if="lesson.duration">{{ formatDuration(lesson.duration) }}</span>
                      <span :class="['status-badge', lesson.is_active ? 'active' : 'inactive']">
                        {{ lesson.is_active ? '공개' : '비공개' }}
                      </span>
                    </div>
                    <div class="item-actions">
                      <button @click="openEditModal(lesson)" title="수정">
                        <i class="ph ph-pencil"></i>
                      </button>
                      <button @click="deleteItem(lesson)" title="삭제" class="danger">
                        <i class="ph ph-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 빈 상태 -->
          <div v-if="treeData.length === 0" class="empty-state">
            <i class="ph ph-folder-open"></i>
            <p>커리큘럼이 없습니다</p>
            <button @click="openAddModal('category', null)">카테고리 추가</button>
          </div>
        </div>
      </div>

    </div>

    <!-- 추가/수정 모달 -->
    <div v-if="modalVisible" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ modalMode === 'add' ? typeLabels[modalType] + ' 추가' : '수정' }}</h3>
          <button @click="closeModal"><i class="ph ph-x"></i></button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>제목</label>
            <input v-model="formData.title" type="text" placeholder="제목을 입력하세요" />
          </div>

          <!-- 레슨 전용 필드 -->
          <template v-if="modalType === 'lesson'">
            <div class="form-group">
              <label>콘텐츠 유형</label>
              <div class="radio-group">
                <label class="radio-item">
                  <input type="radio" v-model="formData.content_type" value="video" />
                  <span><i class="ph ph-play-circle"></i> 영상</span>
                </label>
                <label class="radio-item">
                  <input type="radio" v-model="formData.content_type" value="article" />
                  <span><i class="ph ph-article"></i> 글</span>
                </label>
              </div>
            </div>

            <div class="form-group" v-if="formData.content_type === 'video'">
              <label>영상 URL</label>
              <input v-model="formData.content" type="text" placeholder="YouTube 또는 영상 URL" />
            </div>

            <div class="form-group" v-if="formData.content_type === 'article'">
              <label>글 내용</label>
              <textarea v-model="formData.content" rows="8" placeholder="내용을 입력하세요"></textarea>
            </div>

            <div class="form-group">
              <label>소요시간 (분)</label>
              <input v-model.number="formData.duration_min" type="number" min="0" placeholder="0" />
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="formData.is_active" />
                <span>공개</span>
              </label>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal">취소</button>
          <button class="btn-save" @click="saveItem">저장</button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { inject, ref, onMounted, reactive } from "vue"
import { useBackButton } from '@/utils/useBackBtn'
import CurriculumHistory from './CurriculumHistory.vue'

export default {
  components: {
    CurriculumHistory
  },
  props: {
    windowOnOff: Function,
  },
  setup(props) {
    const sql_query = inject('sql_query')

    useBackButton(() => {
      if (showHistory.value) {
        showHistory.value = false
      } else {
        props.windowOnOff(false)
      }
    })

    const showHistory = ref(false)
    const treeData = ref([])
    const expanded = reactive({})
    const dragItem = ref(null)
    const dragType = ref(null)

    // 모달 관련
    const modalVisible = ref(false)
    const modalMode = ref('add')
    const modalType = ref('category')
    const modalParentIdx = ref(null)
    const editingItem = ref(null)

    const typeLabels = {
      category: '카테고리',
      chapter: '챕터',
      lesson: '레슨'
    }

    const formData = reactive({
      title: '',
      content_type: 'video',
      content: '',
      duration_min: 0,
      is_active: true
    })

    async function fetchData() {
      const items = await sql_query(`SELECT * FROM curriculum_items ORDER BY sort_order ASC`)

      const categories = items.filter(i => i.type === 'category')
      const chapters = items.filter(i => i.type === 'chapter')
      const lessons = items.filter(i => i.type === 'lesson')

      treeData.value = categories.map(cat => ({
        ...cat,
        children: chapters
          .filter(ch => ch.parent_idx === cat.idx)
          .map(ch => ({
            ...ch,
            children: lessons.filter(ls => ls.parent_idx === ch.idx)
          }))
      }))

      categories.forEach(cat => {
        if (expanded[cat.idx] === undefined) expanded[cat.idx] = true
      })
    }

    function toggleExpand(idx) {
      expanded[idx] = !expanded[idx]
    }

    function getChapterCount(category) {
      return category.children?.length || 0
    }

    function getLessonCount(chapter) {
      return chapter.children?.length || 0
    }

    function formatDuration(seconds) {
      const min = Math.floor(seconds / 60)
      return `${min}분`
    }

    function openAddModal(type, parentIdx) {
      modalMode.value = 'add'
      modalType.value = type
      modalParentIdx.value = parentIdx
      editingItem.value = null
      resetForm()
      modalVisible.value = true
    }

    function openEditModal(item) {
      modalMode.value = 'edit'
      modalType.value = item.type
      editingItem.value = item
      formData.title = item.title
      formData.content_type = item.content_type || 'video'
      formData.content = item.content || ''
      formData.duration_min = Math.floor((item.duration || 0) / 60)
      formData.is_active = item.is_active === 1
      modalVisible.value = true
    }

    function closeModal() {
      modalVisible.value = false
      resetForm()
    }

    function resetForm() {
      formData.title = ''
      formData.content_type = 'video'
      formData.content = ''
      formData.duration_min = 0
      formData.is_active = true
    }

    async function saveItem() {
      if (!formData.title.trim()) {
        alert('제목을 입력하세요')
        return
      }

      if (modalMode.value === 'add') {
        let maxSort = 0
        if (modalType.value === 'category') {
          const res = await sql_query(`SELECT MAX(sort_order) as max_sort FROM curriculum_items WHERE type = 'category'`)
          maxSort = (res[0]?.max_sort || 0) + 1
        } else {
          const res = await sql_query(`SELECT MAX(sort_order) as max_sort FROM curriculum_items WHERE parent_idx = ${modalParentIdx.value}`)
          maxSort = (res[0]?.max_sort || 0) + 1
        }

        const insertData = {
          parent_idx: modalParentIdx.value,
          type: modalType.value,
          title: formData.title,
          sort_order: maxSort
        }

        if (modalType.value === 'lesson') {
          insertData.content_type = formData.content_type
          insertData.content = formData.content
          insertData.duration = formData.duration_min * 60
          insertData.is_active = formData.is_active ? 1 : 0
        }

        const fields = Object.keys(insertData).join(', ')
        const values = Object.values(insertData).map(v => 
          v === null ? 'NULL' : typeof v === 'string' ? `'${v.replace(/'/g, "''")}'` : v
        ).join(', ')

        await sql_query(`INSERT INTO curriculum_items (${fields}) VALUES (${values})`)
      } else {
        let updateSql = `UPDATE curriculum_items SET title = '${formData.title.replace(/'/g, "''")}'`

        if (modalType.value === 'lesson') {
          updateSql += `, content_type = '${formData.content_type}'`
          updateSql += `, content = '${(formData.content || '').replace(/'/g, "''")}'`
          updateSql += `, duration = ${formData.duration_min * 60}`
          updateSql += `, is_active = ${formData.is_active ? 1 : 0}`
        }

        updateSql += ` WHERE idx = ${editingItem.value.idx}`
        await sql_query(updateSql)
      }

      closeModal()
      await fetchData()
    }

    async function deleteItem(item) {
      const label = typeLabels[item.type]
      const hasChildren = item.children && item.children.length > 0

      let message = `"${item.title}" ${label}을(를) 삭제하시겠습니까?`
      if (hasChildren) {
        message += `\n\n⚠️ 하위 항목도 모두 삭제됩니다.`
      }

      if (!confirm(message)) return

      await sql_query(`DELETE FROM curriculum_items WHERE idx = ${item.idx}`)
      await fetchData()
    }

    function onDragStart(e, item, type) {
      dragItem.value = item
      dragType.value = type
      e.dataTransfer.effectAllowed = 'move'
    }

    function onDragOver(e, targetItem, targetType) {
      if (!dragItem.value) return
      if (dragType.value !== targetType) return
      if (dragItem.value.idx === targetItem.idx) return
      
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }

    async function onDrop(e, targetItem, targetType) {
      e.preventDefault()
      if (!dragItem.value) return
      if (dragType.value !== targetType) return
      if (dragItem.value.idx === targetItem.idx) return
      if (dragItem.value.parent_idx !== targetItem.parent_idx) return

      const dragSort = dragItem.value.sort_order
      const targetSort = targetItem.sort_order

      await sql_query(`UPDATE curriculum_items SET sort_order = ${targetSort} WHERE idx = ${dragItem.value.idx}`)
      await sql_query(`UPDATE curriculum_items SET sort_order = ${dragSort} WHERE idx = ${targetItem.idx}`)

      dragItem.value = null
      dragType.value = null
      await fetchData()
    }

    function onDragEnd() {
      dragItem.value = null
      dragType.value = null
    }

    onMounted(() => {
      fetchData()
    })

    return {
      treeData,
      expanded,
      toggleExpand,
      getChapterCount,
      getLessonCount,
      formatDuration,
      modalVisible,
      modalMode,
      modalType,
      typeLabels,
      formData,
      openAddModal,
      openEditModal,
      closeModal,
      saveItem,
      deleteItem,
      dragItem,
      onDragStart,
      onDragOver,
      onDrop,
      onDragEnd,
      showHistory
    }
  }
}
</script>


<style scoped>
#dialog-curriculumAdmin {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  background-color: var(--color-background);
  z-index: var(--z-index-middle);
}

.curriculum-admin {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.curriculum-admin > .header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  color: var(--color-text-primary);
  padding: 16px;
  box-sizing: border-box;
}

.curriculum-admin > .header > p {
  aspect-ratio: 1/1;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  cursor: pointer;
  margin: 0;
  padding: 0;
}

.curriculum-admin > .header > h2 {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
}

.content {
  width: 100%;
  max-width: 800px;
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  box-sizing: border-box;
}

/* 액션 바 */
.action-bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.add-category-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: var(--color-brand);
  color: var(--color-text-white);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.15s ease;
}

.add-category-btn:hover {
  filter: brightness(1.1);
}

.add-category-btn i {
  font-size: var(--font-size-md);
}

/* 트리 구조 */
.tree-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tree-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--color-background-light);
  border-radius: var(--radius-md);
  transition: all 0.15s ease;
}

.tree-item:hover {
  background: var(--color-surface-reverse);
}

.tree-item.dragging {
  opacity: 0.5;
  transform: scale(0.98);
}

.category-item {
  background: var(--color-brand);
  color: var(--color-text-white);
}

.category-item:hover {
  background: var(--color-brand);
  filter: brightness(1.1);
}

.chapter-item {
  margin-left: 24px;
}

.lesson-item {
  margin-left: 48px;
  padding: 10px 16px;
}

.item-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.drag-handle {
  cursor: grab;
  opacity: 0.5;
  font-size: var(--font-size-lg);
}

.drag-handle:hover {
  opacity: 1;
}

.toggle-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
}

.type-icon {
  font-size: var(--font-size-lg);
  opacity: 0.7;
}

.item-title {
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-count {
  font-size: var(--font-size-xs);
  opacity: 0.7;
  white-space: nowrap;
}

.item-duration {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.status-badge {
  font-size: var(--font-size-xs);
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}

.status-badge.active {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.status-badge.inactive {
  background: rgba(218, 47, 71, 0.2);
  color: var(--color-special-warning);
}

.item-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.tree-item:hover .item-actions {
  opacity: 1;
}

.item-actions button {
  background: var(--color-surface);
  border: none;
  padding: 6px 8px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  color: inherit;
  transition: background 0.15s ease;
}

.item-actions button:hover {
  background: var(--color-surface-reverse);
}

.item-actions button.danger:hover {
  background: rgba(218, 47, 71, 0.3);
  color: var(--color-special-warning);
}

.tree-children {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--color-text-secondary);
  gap: 16px;
}

.empty-state i {
  font-size: 48px;
  opacity: 0.5;
}

.empty-state button {
  padding: 10px 20px;
  background: var(--color-brand);
  color: var(--color-text-white);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: var(--font-weight-medium);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface-mega);
  backdrop-filter: blur(var(--effect-blur));
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-top);
  padding: 16px;
  box-sizing: border-box;
}

.modal-content {
  background: var(--color-background);
  border-radius: var(--radius-lg);
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-md);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--color-text-third);
}

.modal-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
}

.modal-header button {
  background: none;
  border: none;
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group > label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group textarea {
  padding: 12px;
  border: 1px solid var(--color-text-third);
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--color-brand);
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
}

.radio-group {
  display: flex;
  gap: 12px;
}

.radio-item {
  flex: 1;
  cursor: pointer;
}

.radio-item input {
  display: none;
}

.radio-item span {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border: 2px solid var(--color-text-third);
  border-radius: var(--radius-md);
  transition: all 0.15s ease;
  color: var(--color-text-primary);
}

.radio-item input:checked + span {
  border-color: var(--color-brand);
  background: rgba(255, 165, 0, 0.1);
  color: var(--color-brand);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: var(--color-text-primary);
}

.checkbox-label input {
  width: 18px;
  height: 18px;
  accent-color: var(--color-brand);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid var(--color-text-third);
}

.btn-cancel {
  padding: 10px 20px;
  border: 1px solid var(--color-text-third);
  background: transparent;
  color: var(--color-text-primary);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: var(--font-weight-medium);
}

.btn-save {
  padding: 10px 20px;
  border: none;
  background: var(--color-brand);
  color: var(--color-text-white);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: var(--font-weight-medium);
}

@media (max-width: 768px) {
  .chapter-item {
    margin-left: 16px;
  }
  .lesson-item {
    margin-left: 32px;
  }
}
</style>